import React, { useEffect, useState } from "react";
import { INSTALLATION_JOB_STORAGE_KEY } from "../InstallationJob";
import { GetLocationsType } from "../../../models/_publicCall";
import SimpleMobileStepper from "../../Stepper/Stepper";
import { InstallationJobStepsList } from "../InstallationSteps";
import { Tooltip } from "react-tooltip";

interface LocationContactsProps {
  contactTypeDB: { id: string; name: string }[];
  setCurrentStep: (step: number) => void;
  InstalltionFormSteps: any;
  storedData: any;
  locationsDB: GetLocationsType[];
  jobStatus: string;
  installationJobDB: any;
  installationJobStepsRequired: { id: number; label: string }[];
  isFormSubmitted: boolean;
  setIsFormSubmitted: (submitted: boolean) => void;
}

const LocationContacts: React.FC<LocationContactsProps> = ({
  contactTypeDB,
  setCurrentStep,
  InstalltionFormSteps,
  storedData,
  locationsDB,
  jobStatus,
  installationJobDB,
  installationJobStepsRequired,
  isFormSubmitted,
  setIsFormSubmitted,
}) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const selectedLocationId = storedData?.locationSelection?.id;
  const matchedRecord = locationsDB?.find(
    (loc) => loc.id === selectedLocationId,
  );

  // Clear errors helper
  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // ------------------ Contact States ------------------
  const [hasSecondContact, setHasSecondContact] = useState(false);
  const [secondContact, setSecondContact] = useState({
    name: "",
    role: "",
    phone: "",
    email: "",
    phoneType: "",
  });

  const [hasMarketingContact, setHasMarketingContact] = useState(false);
  const [marketingContact, setMarketingContact] = useState({
    name: "",
    role: "",
    phone: "",
    email: "",
    phoneType: "",
  });

  const [hasItContact, setHasItContact] = useState(false);
  const [itContact, setItContact] = useState({
    name: "",
    role: "",
    phone: "",
    email: "",
    phoneType: "",
  });

  const [hasServiceContact, setHasServiceContact] = useState(false);
  const [serviceContact, setServiceContact] = useState({
    name: "",
    role: "",
    phone: "",
    email: "",
    phoneType: "",
  });

  const [hasStoreManagerContact, setHasStoreManagerContact] = useState(false);
  const [storeManagerContact, setStoreManagerContact] = useState({
    name: "",
    phone: "",
    email: "",
    phoneType: "",
  });

  // ------------------ Validation ------------------
  const validateContact = (contact: any, prefix: string) => {
    let newErrors: { [key: string]: string } = {};
    if (!contact.name) newErrors[`${prefix}Name`] = "Name is required";
    if (!contact.role && prefix !== "storeManager")
      newErrors[`${prefix}Role`] = "Role is required";
    if (!contact.phone) newErrors[`${prefix}Phone`] = "Phone is required";
    if (!contact.email) newErrors[`${prefix}Email`] = "Email is required";
    if (!contact.phoneType)
      newErrors[`${prefix}PhoneType`] = "Phone type is required";
    return newErrors;
  };

  const validateForm = () => {
    let newErrors: { [key: string]: string } = {};
    if (hasSecondContact)
      Object.assign(newErrors, validateContact(secondContact, "second"));
    if (hasMarketingContact)
      Object.assign(newErrors, validateContact(marketingContact, "marketing"));
    if (hasItContact)
      Object.assign(newErrors, validateContact(itContact, "it"));
    if (hasServiceContact)
      Object.assign(newErrors, validateContact(serviceContact, "service"));
    if (hasStoreManagerContact)
      Object.assign(
        newErrors,
        validateContact(storeManagerContact, "storeManager"),
      );

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ------------------ Save ------------------
  const handleNext = () => {
    if (!validateForm()) return;

    const locationContacts = {
      hasSecondContact,
      hasMarketingContact,
      hasItContact,
      hasServiceContact,
      hasStoreManagerContact,
      secondContact: hasSecondContact ? secondContact : null,
      marketingContact: hasMarketingContact ? marketingContact : null,
      itContact: hasItContact ? itContact : null,
      serviceContact: hasServiceContact ? serviceContact : null,
      storeManagerContact: hasStoreManagerContact ? storeManagerContact : null,
    };

    // Get current storage and update only for this location
    const storedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
    );
    const updatedData = {
      ...storedData,
      locationContactsById: {
        ...(storedData.locationContactsById || {}),
        [selectedLocationId]: locationContacts,
      },
    };
    localStorage.setItem(
      INSTALLATION_JOB_STORAGE_KEY,
      JSON.stringify(updatedData),
    );
    setIsFormSubmitted(true);
    const form = document.querySelector("form");
    if (form) form.requestSubmit();
    setCurrentStep(InstalltionFormSteps.PICTURES_AND_VIDEO);
  };

  // ------------------ Load - prioritize localStorage ------------------
  useEffect(() => {
    const savedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
    );

    setHasSecondContact(false);
    setSecondContact({
      name: "",
      role: "",
      phone: "",
      email: "",
      phoneType: "",
    });
    setHasMarketingContact(false);
    setMarketingContact({
      name: "",
      role: "",
      phone: "",
      email: "",
      phoneType: "",
    });
    setHasItContact(false);
    setItContact({ name: "", role: "", phone: "", email: "", phoneType: "" });
    setHasServiceContact(false);
    setServiceContact({
      name: "",
      role: "",
      phone: "",
      email: "",
      phoneType: "",
    });
    setHasStoreManagerContact(false);
    setStoreManagerContact({ name: "", phone: "", email: "", phoneType: "" });

    const lc = savedData?.locationContactsById?.[selectedLocationId];
    if (lc) {
      if (lc.hasSecondContact && lc.secondContact) {
        setHasSecondContact(true);
        setSecondContact(lc.secondContact);
      }
      if (lc.hasMarketingContact && lc.marketingContact) {
        setHasMarketingContact(true);
        setMarketingContact(lc.marketingContact);
      }
      if (lc.hasItContact && lc.itContact) {
        setHasItContact(true);
        setItContact(lc.itContact);
      }
      if (lc.hasServiceContact && lc.serviceContact) {
        setHasServiceContact(true);
        setServiceContact(lc.serviceContact);
      }
      if (lc.hasStoreManagerContact && lc.storeManagerContact) {
        setHasStoreManagerContact(true);
        setStoreManagerContact(lc.storeManagerContact);
      }
      return;
    }

    if (jobStatus !== "Completed" && installationJobDB) {
      if (installationJobDB.customerSecondContact) {
        setHasSecondContact(installationJobDB.customerSecondContact);
        setSecondContact({
          name: installationJobDB.secondContactName || "",
          role: installationJobDB.secondContactRole || "",
          phone: installationJobDB.secondContactPhone || "",
          email: installationJobDB.secondContactEmail || "",
          phoneType: installationJobDB.secondContactPhoneType || "",
        });
      }

      if (installationJobDB.customerMarketingContact) {
        setHasMarketingContact(installationJobDB.customerMarketingContact);
        setMarketingContact({
          name: installationJobDB.marketingContactName || "",
          role: installationJobDB.marketingContactRole || "",
          phone: installationJobDB.marketingContactPhone || "",
          email: installationJobDB.marketingContactEmail || "",
          phoneType: installationJobDB.marketingContactPhoneType || "",
        });
      }

      if (installationJobDB.customerItContact) {
        setHasItContact(installationJobDB.customerItContact);
        setItContact({
          name: installationJobDB.itContactName || "",
          role: installationJobDB.itContactRole || "",
          phone: installationJobDB.itContactPhone || "",
          email: installationJobDB.itContactEmail || "",
          phoneType: installationJobDB.itContactPhoneType || "",
        });
      }

      if (installationJobDB.customerServiceContact) {
        setHasServiceContact(installationJobDB.customerServiceContact);
        setServiceContact({
          name: installationJobDB.serviceContactName || "",
          role: installationJobDB.serviceContactRole || "",
          phone: installationJobDB.serviceContactPhone || "",
          email: installationJobDB.serviceContactEmail || "",
          phoneType: installationJobDB.serviceContactPhoneType || "",
        });
      }

      if (installationJobDB.storeManagerContact) {
        setHasStoreManagerContact(installationJobDB.storeManagerContact);
        setStoreManagerContact({
          name: installationJobDB.storeManagerContactName || "",
          phone: installationJobDB.storeManagerContactPhone || "",
          email: installationJobDB.storeManagerContactEmail || "",
          phoneType: installationJobDB.storeManagerContactPhoneType || "",
        });
      }

      // Save to localStorage for future use
      const contactsData = {
        hasSecondContact: installationJobDB.customerSecondContact || false,
        hasMarketingContact:
          installationJobDB.customerMarketingContact || false,
        hasItContact: installationJobDB.customerItContact || false,
        hasServiceContact: installationJobDB.customerServiceContact || false,
        hasStoreManagerContact: installationJobDB.storeManagerContact || false,
        secondContact: installationJobDB.customerSecondContact
          ? {
              name: installationJobDB.secondContactName || "",
              role: installationJobDB.secondContactRole || "",
              phone: installationJobDB.secondContactPhone || "",
              email: installationJobDB.secondContactEmail || "",
              phoneType: installationJobDB.secondContactPhoneType || "",
            }
          : null,
        marketingContact: installationJobDB.customerMarketingContact
          ? {
              name: installationJobDB.marketingContactName || "",
              role: installationJobDB.marketingContactRole || "",
              phone: installationJobDB.marketingContactPhone || "",
              email: installationJobDB.marketingContactEmail || "",
              phoneType: installationJobDB.marketingContactPhoneType || "",
            }
          : null,
        itContact: installationJobDB.customerItContact
          ? {
              name: installationJobDB.itContactName || "",
              role: installationJobDB.itContactRole || "",
              phone: installationJobDB.itContactPhone || "",
              email: installationJobDB.itContactEmail || "",
              phoneType: installationJobDB.itContactPhoneType || "",
            }
          : null,
        serviceContact: installationJobDB.customerServiceContact
          ? {
              name: installationJobDB.serviceContactName || "",
              role: installationJobDB.serviceContactRole || "",
              phone: installationJobDB.serviceContactPhone || "",
              email: installationJobDB.serviceContactEmail || "",
              phoneType: installationJobDB.serviceContactPhoneType || "",
            }
          : null,
        storeManagerContact: installationJobDB.storeManagerContact
          ? {
              name: installationJobDB.storeManagerContactName || "",
              phone: installationJobDB.storeManagerContactPhone || "",
              email: installationJobDB.storeManagerContactEmail || "",
              phoneType: installationJobDB.storeManagerContactPhoneType || "",
            }
          : null,
      };

      const updatedData = {
        ...savedData,
        locationContactsById: {
          ...(savedData.locationContactsById || {}),
          [selectedLocationId]: contactsData,
        },
      };
      localStorage.setItem(
        INSTALLATION_JOB_STORAGE_KEY,
        JSON.stringify(updatedData),
      );
      return; // Don't override with location DB data if we have installationJobDB data
    }

    // 4️⃣ Load from matchedRecord (Location DB) - only if no localStorage or installationJobDB data
    if (matchedRecord) {
      if (matchedRecord.customer_second_contact) {
        setHasSecondContact(true);
        setSecondContact({
          name: matchedRecord.second_contact_name || "",
          role: matchedRecord.second_contact_name_role || "",
          phone: matchedRecord.second_contact_phone_number || "",
          email: matchedRecord.second_contact_email || "",
          phoneType: matchedRecord.type_of_second_contact_phone_number || "",
        });
      }
      if (matchedRecord.customer_marketing_contact) {
        setHasMarketingContact(true);
        setMarketingContact({
          name: matchedRecord.marketing_contact_name || "",
          role: matchedRecord.marketing_contact_name_role || "",
          phone: matchedRecord.marketing_contact_phone_number || "",
          email: matchedRecord.marketing_contact_email || "",
          phoneType: matchedRecord.type_of_marketing_contact_phone_number || "",
        });
      }
      if (matchedRecord.customer_it_contact) {
        setHasItContact(true);
        setItContact({
          name: matchedRecord.it_contact_name || "",
          role: matchedRecord.it_contact_name_role || "",
          phone: matchedRecord.it_contact_phone_number || "",
          email: matchedRecord.it_contact_email || "",
          phoneType: matchedRecord.type_of_it_contact_phone_number || "",
        });
      }
      if (matchedRecord.customer_service_contact) {
        setHasServiceContact(true);
        setServiceContact({
          name: matchedRecord.service_contact_name || "",
          role: matchedRecord.service_contact_name_role || "",
          phone: matchedRecord.service_contact_phone_number || "",
          email: matchedRecord.service_contact_email || "",
          phoneType: matchedRecord.type_of_service_contact_phone_number || "",
        });
      }
      if (matchedRecord.store_manager_contact) {
        setHasStoreManagerContact(true);
        setStoreManagerContact({
          name: matchedRecord.store_manager_contact_name || "",
          phone: matchedRecord.store_manager_contact_phone_number || "",
          email: matchedRecord.store_manager_contact_email || "",
          phoneType:
            matchedRecord.type_of_store_manager_contact_phone_number || "",
        });
      }
    }
  }, [selectedLocationId, matchedRecord, jobStatus, installationJobDB]);

  // ------------------ Render Helper ------------------
  const renderContactForm = (
    title: string,
    hasContact: boolean,
    setHasContact: (v: boolean) => void,
    contact: any,
    setContact: (v: any) => void,
    prefix: string,
    includeRole = true,
  ) => (
    <>
      <div className="row mt-4">
        <div className="col-lg-6">
          <label className="form-check d-flex align-items-center">
            <input
              type="checkbox"
              className="form-check-input"
              checked={hasContact}
              onChange={(e) => setHasContact(e.target.checked)}
            />
            <span
              className={`form-label fw-bold fs-3 ms-2 location-contact-${prefix}-label`}
            >
              Add {title}
            </span>
            <Tooltip
              anchorSelect={`.location-contact-${prefix}-label`}
              data-placement="top"
            >
              {title === "Second Contact" &&
                "Add an optional secondary point of contact at the location"}
              {title === "Marketing Contact" &&
                "Add marketing department contact information"}
              {title === "IT Contact" &&
                "Add IT department contact information"}
              {title === "Service Contact" &&
                "Add service department contact information"}
              {title === "Store Manager Contact" &&
                "Add store manager contact information"}
            </Tooltip>
          </label>
        </div>
      </div>

      {hasContact && (
        <div className="row mt-2">
          <div className="col-lg-6">
            <label className="form-label fw-bold">Name</label>
            <input
              className="form-control"
              value={contact.name}
              onChange={(e) => {
                setContact({ ...contact, name: e.target.value });
                clearError(`${prefix}Name`);
              }}
            />
            {errors[`${prefix}Name`] && (
              <small className="text-danger">{errors[`${prefix}Name`]}</small>
            )}
          </div>

          {includeRole && (
            <div className="col-lg-6">
              <label className="form-label fw-bold">Role</label>
              <input
                className="form-control"
                value={contact.role}
                onChange={(e) => {
                  setContact({ ...contact, role: e.target.value });
                  clearError(`${prefix}Role`);
                }}
              />
              {errors[`${prefix}Role`] && (
                <small className="text-danger">{errors[`${prefix}Role`]}</small>
              )}
            </div>
          )}

          <div className="col-lg-6 mt-2">
            <label className="form-label fw-bold">Phone</label>
            <input
              className="form-control"
              value={contact.phone}
              onChange={(e) => {
                setContact({ ...contact, phone: e.target.value });
                clearError(`${prefix}Phone`);
              }}
            />
            {errors[`${prefix}Phone`] && (
              <small className="text-danger">{errors[`${prefix}Phone`]}</small>
            )}
          </div>

          <div className="col-lg-6 mt-2">
            <label className="form-label fw-bold">Email</label>
            <input
              className="form-control"
              value={contact.email}
              onChange={(e) => {
                setContact({ ...contact, email: e.target.value });
                clearError(`${prefix}Email`);
              }}
            />
            {errors[`${prefix}Email`] && (
              <small className="text-danger">{errors[`${prefix}Email`]}</small>
            )}
          </div>

          <div className="col-lg-12 mt-2">
            <label className="form-label fw-bold">Phone Type</label>
            <div className="row">
              {contactTypeDB.map((type) => (
                <div key={type.id} className="col-6">
                  <label className="form-check d-flex align-items-center">
                    <input
                      className="form-check-input me-2"
                      type="radio"
                      name={`${prefix}PhoneType`}
                      value={type.id}
                      checked={contact.phoneType === type.id}
                      onChange={() =>
                        setContact({ ...contact, phoneType: type.id })
                      }
                    />
                    <span>{type.name}</span>
                  </label>
                </div>
              ))}
            </div>
            {errors[`${prefix}PhoneType`] && (
              <small className="text-danger">
                {errors[`${prefix}PhoneType`]}
              </small>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div>
      <header className="header">
        <h2>Location Contacts</h2>
      </header>

      {/* <div
        className="left-icon m-4 mt-4"
        onClick={() => setCurrentStep(InstalltionFormSteps.LOCATION_INFORMATION)}
        style={{ cursor: "pointer" }}
      >
        <i className="fa fa-arrow-left" aria-hidden="true"></i>
      </div> */}

      <div className="job-wrapper mt-8">
        {renderContactForm(
          "Second Contact",
          hasSecondContact,
          setHasSecondContact,
          secondContact,
          setSecondContact,
          "second",
        )}
        {renderContactForm(
          "Marketing Contact",
          hasMarketingContact,
          setHasMarketingContact,
          marketingContact,
          setMarketingContact,
          "marketing",
        )}
        {renderContactForm(
          "IT Contact",
          hasItContact,
          setHasItContact,
          itContact,
          setItContact,
          "it",
        )}
        {renderContactForm(
          "Service Contact",
          hasServiceContact,
          setHasServiceContact,
          serviceContact,
          setServiceContact,
          "service",
        )}
        {renderContactForm(
          "Store Manager Contact",
          hasStoreManagerContact,
          setHasStoreManagerContact,
          storeManagerContact,
          setStoreManagerContact,
          "storeManager",
          false,
        )}

        {/* <div className="text-end mt-4">
          <button type="button" className="btn btn-primary" onClick={handleNext}>
            <i className="fa fa-long-arrow-right"></i> Next
          </button>
        </div> */}
      </div>

      <SimpleMobileStepper
        totalSteps={installationJobStepsRequired.length}
        handleNext={handleNext}
        handleBack={() =>
          setCurrentStep(InstalltionFormSteps.LOCATION_INFORMATION)
        }
        activeStep={installationJobStepsRequired.findIndex(
          (s) => s.id === InstalltionFormSteps.LOCATION_CONTACTS,
        )}
        isFormSubmitted={isFormSubmitted}
        isLastStep={true}
      />
    </div>
  );
};

export default LocationContacts;
